import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Select, formatPrice } from '@artosera/ui';
import { ETATS_CONTRAT_LOCATION, type EtatContratLocation } from '@artosera/shared';
import { useLocations, useVueLocations } from '../../api/locations.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import styles from './Locations.module.css';
import { BoutonVerrouille } from '../module/LectureSeule.js';

export function LocationsPage() {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [etat, setEtat] = useState<EtatContratLocation | undefined>();
  const [page, setPage] = useState(1);
  const liste = useLocations(galerieSlug, { ...(etat ? { etat } : {}), page });
  const actifs = useLocations(galerieSlug, { etat: 'actif' });
  const vue = useVueLocations(galerieSlug);
  const prix = (centimes: number) =>
    formatPrice(centimes, i18n.language === 'en' ? 'en' : 'fr', 'EUR');
  const maximum = Math.max(1, ...(vue.data?.serieMrr.map((m) => m.montantHtCentimes) ?? []));
  return (
    <section className={styles.page} data-ui="page">
      <header className={styles.entete}>
        <h1>{t('locations.titre')}</h1>
        <div className={styles.actions}>
          <Link to={`/${galerieSlug}/locations/echeances`}>{t('locations.echeances')}</Link>
          {galerieALaction(galerie, 'locations:write') ? (
            <Link to={`/${galerieSlug}/locations/nouveau`}>{t('locations.nouveau')}</Link>
          ) : (
            <BoutonVerrouille action="locations:write">{t('locations.nouveau')}</BoutonVerrouille>
          )}
        </div>
      </header>
      {(liste.isPending || vue.isPending) && <p role="status">{t('commun:chargement')}</p>}
      {(liste.isError || vue.isError || actifs.isError) && (
        <p role="alert">{t('commun:erreurReseau')}</p>
      )}
      {vue.data && (
        <>
          <div className={styles.cartes}>
            {[
              [t('locations.mrr'), prix(vue.data.mrrHtCentimes)],
              [t('locations.actifs'), actifs.data?.pagination.total ?? '—'],
              [t('locations.oeuvresLouees'), vue.data.oeuvresLouees.length],
              [t('locations.aVenir'), vue.data.echeancesAVenir.length],
              [t('locations.options'), vue.data.optionsOuvertes.length],
            ].map(([libelle, valeur]) => (
              <div className={styles.carte} key={libelle}>
                <p>{libelle}</p>
                <strong>{valeur}</strong>
              </div>
            ))}
          </div>
          <section>
            <h2>{t('locations.evolution')}</h2>
            <ul className={styles.serie}>
              {vue.data.serieMrr.map((m) => (
                <li className={styles.mois} key={m.mois}>
                  <span>{m.mois}</span>
                  <span
                    className={styles.barre}
                    style={{ width: `${(m.montantHtCentimes * 100) / maximum}%` }}
                  />
                  <span>{prix(m.montantHtCentimes)}</span>
                </li>
              ))}
            </ul>
          </section>
          <div className={styles.cartes}>
            <section>
              <h2>{t('locations.oeuvresLouees')}</h2>
              {vue.data.oeuvresLouees.map((l) => (
                <p key={l.ligneId}>
                  <Link to={`/${galerieSlug}/oeuvres/${l.oeuvreId}`}>{l.titre}</Link>
                </p>
              ))}
            </section>
            <section>
              <h2>{t('locations.aVenir')}</h2>
              {vue.data.echeancesAVenir.map((e) => (
                <p key={`${e.contratId}:${e.debutPeriode}`}>
                  <Link to={`/${galerieSlug}/locations/${e.contratId}`}>{e.numero}</Link> —{' '}
                  {e.dateFacturation}
                </p>
              ))}
            </section>
            <section>
              <h2>{t('locations.options')}</h2>
              {vue.data.optionsOuvertes.map((o) => (
                <p key={`${o.contratId}:${o.ligneId}`}>
                  <Link to={`/${galerieSlug}/locations/${o.contratId}`}>
                    {prix(o.option.prixConvenuCentimes)}
                  </Link>{' '}
                  — {o.option.valableJusquAu}
                </p>
              ))}
            </section>
          </div>
        </>
      )}
      <Select
        aria-label={t('locations.etat')}
        value={etat ?? 'tous'}
        onValueChange={(v) => {
          setEtat(v === 'tous' ? undefined : (v as EtatContratLocation));
          setPage(1);
        }}
        options={[
          { value: 'tous', label: t('locations.tous') },
          ...ETATS_CONTRAT_LOCATION.map((v) => ({ value: v, label: t(`locations.etats.${v}`) })),
        ]}
      />
      <div className={styles.liste}>
        {liste.data?.contrats.map((c) => (
          <Link className={styles.ligne} key={c.id} to={`/${galerieSlug}/locations/${c.id}`}>
            <strong>{c.numero}</strong>
            <span>{t(`locations.etats.${c.etatEffectif}`)}</span>
            <span>{c.lignes.map((l) => l.titre).join(', ')}</span>
          </Link>
        ))}
      </div>
      {liste.data?.contrats.length === 0 && <p>{t('locations.aucun')}</p>}
      {liste.data && liste.data.pagination.pages > 1 && (
        <nav className={styles.actions}>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
            {t('locations.precedent')}
          </Button>
          <span>
            {page} / {liste.data.pagination.pages}
          </span>
          <Button disabled={page >= liste.data.pagination.pages} onClick={() => setPage(page + 1)}>
            {t('locations.suivant')}
          </Button>
        </nav>
      )}
    </section>
  );
}
